import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyEmailRequest, resendVerificationRequest, clearError } from '../../redux/actions/authActions';
import './Auth.css';

function EmailVerification() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { loading, error, emailVerified, verificationEmailSent } = useSelector(state => state.auth);

  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState('verify'); // 'verify' or 'resend'

  useEffect(() => {
    // Get token from URL parameters
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
      // Automatically verify email if token is present
      dispatch(verifyEmailRequest(urlToken));
    } else {
      // Show resend form if no token
      setMode('resend');
    }
    
    // Clear any existing errors when component mounts
    dispatch(clearError());
  }, [searchParams, dispatch]);

  useEffect(() => {
    // Redirect to login on successful email verification
    if (emailVerified) {
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [emailVerified, navigate]);

  const handleResendVerification = (e) => {
    e.preventDefault();
    if (email) {
      dispatch(resendVerificationRequest(email));
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  if (emailVerified) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h2>Email Verified Successfully</h2>
          <div className="success-message">
            <p>Your email has been successfully verified!</p>
            <p>You can now access all features of your account.</p>
            <p>Redirecting to login page...</p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationEmailSent) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h2>Verification Email Sent</h2>
          <div className="success-message">
            <p>A new verification email has been sent to your email address.</p>
            <p>Please check your email and click the verification link.</p>
          </div>
          <p className="auth-link">
            <Link to="/login">Back to Login</Link>
          </p>
        </div>
      </div>
    );
  }

  if (mode === 'verify' && token) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h2>Verifying Email</h2>
          {loading && (
            <div className="loading-message">
              <p>Verifying your email address...</p>
            </div>
          )}
          {error && (
            <div className="error-container">
              <div className="error-message auth-error">{error}</div>
              <p className="auth-description">
                The verification link may be expired or invalid.
              </p>
              <button 
                className="auth-button secondary"
                onClick={() => setMode('resend')}
              >
                Request New Verification Email
              </button>
              <p className="auth-link">
                <Link to="/login">Back to Login</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Resend verification mode
  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2>Resend Email Verification</h2>
        <p className="auth-description">
          Enter your email address to receive a new verification link.
        </p>
        
        {error && <div className="error-message auth-error">{error}</div>}
        
        <form onSubmit={handleResendVerification} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={handleEmailChange}
              required
              disabled={loading}
              placeholder="Enter your email address"
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading || !email}
          >
            {loading ? 'Sending...' : 'Send Verification Email'}
          </button>
          
          <p className="auth-link">
            Already verified? <Link to="/login">Login</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default EmailVerification;